import type { ReactNode } from 'react'
import { RawButton } from '@/components/Button'
import styles from './DragAndDropOverlay.module.css'
import { byDesign } from '@/infra/design'
import imgDragDropUrl from '@/assets/misc/img_drag-drop.svg'
import DragAndUploadIllustration from '@/components/SpotIllustration/DragAndUploadIllustration'

export interface DragAndDropOverlayProps {
  isHidden: boolean
  isDragging: boolean
  isUploading: boolean
  isResolved: boolean
  currentlyUploadingFileName?: string
  remainingCount: number
  successUploadCount: number
  firstSuccessUploadName?: string
  targetJobDisplayName: string
  onView?: () => void
}

export function DragAndDropOverlay({
  isHidden,
  isDragging,
  isUploading,
  isResolved,
  currentlyUploadingFileName,
  remainingCount,
  successUploadCount,
  firstSuccessUploadName,
  targetJobDisplayName,
  onView,
}: DragAndDropOverlayProps) {
  if (isHidden) {
    return null
  }

  if (isDragging) {
    return <DraggingOverlay targetJobDisplayName={targetJobDisplayName} />
  }

  return (
    <div>
      {isUploading && (
        <UploadBar>
          <div className={styles.text}>
            Uploading {currentlyUploadingFileName}
            <span className={styles.remainingCount}>
              &nbsp;
              {remainingCount} documents left
            </span>
          </div>
        </UploadBar>
      )}
      {isResolved && successUploadCount > 0 && (
        <UploadBar>
          <div className={styles.text}>
            {successUploadCount > 1 && (
              <>
                Uploaded {successUploadCount} files to {targetJobDisplayName}
              </>
            )}
            {successUploadCount === 1 && (
              <>
                Uploaded {firstSuccessUploadName} to {targetJobDisplayName}
              </>
            )}
          </div>
          {onView && (
            <div className={styles.action}>
              <RawButton className={styles.viewButton} onPress={onView}>
                VIEW
              </RawButton>
            </div>
          )}
        </UploadBar>
      )}
    </div>
  )
}

interface UploadBarProps {
  children: ReactNode
}

function UploadBar({ children }: UploadBarProps) {
  return <div className={styles.uploadBar}>{children}</div>
}

interface DraggingOverlayProps {
  targetJobDisplayName: string
}

function DraggingOverlay({ targetJobDisplayName }: DraggingOverlayProps) {
  return (
    <div className={styles.dragAndDropOverlay}>
      {byDesign(
        <img alt="" className={styles.dragAndDropImage} src={imgDragDropUrl} />,
        <DragAndUploadIllustration className={styles.dragAndDropImage} />,
      )}
      <h2 className={styles.dragAndDropTitle}>
        Upload to {targetJobDisplayName}
      </h2>
      <p className={styles.dragAndDropDescription}>
        Drag and drop your files anywhere on the screen to intiate file upload.
      </p>
    </div>
  )
}
